/* =========================================================
   Qué le falta a un socio, por su nombre.

   `progreso.ts` contesta CUÁNTO falta. Esto contesta QUÉ falta, que es la
   pregunta que de verdad hace la gente. El portal decía «te faltan 13» y
   no decía cuáles: para averiguarlo había que recorrer siete pestañas,
   cada sede y cada instructor buscando puntos rojos.

   Se apoya en `camposPendientes()`, que ya existía y no la llamaba nadie.
   Lo que añade es DÓNDE están esos campos —qué instructores cuelgan de un
   site, qué sites de una organización, qué documentos hay— y cómo se
   llaman en la lengua de quien mira.

   Va aparte de `progresoPortal.ts` porque aquello resuelve números y esto
   resuelve listas, y porque quien toque una no debería tener que entender
   la otra.
   ========================================================= */
import type { Instructor, Lang, Site } from '@/types';
import { ds } from '@/data';
import { camposPendientes } from './progreso';
import { instructorSchema, orgSchema, siteSchema } from './fields';
import type { EntidadNA } from '../../db/columns';

/** Un campo que falta, ya con su nombre y su sección. */
export interface Falta {
  campo: string;
  /** Cómo se llama en pantalla. */
  etiqueta: string;
  /** La sección donde vive, para saber a qué pestaña ir. */
  seccion: string;
  /** Los obligatorios van primero y en rojo: son los que bloquean. */
  obligatorio: boolean;
}

/** Las faltas de una persona concreta. */
export interface FaltasDeInstructor {
  id: string;
  nombre: string;
  faltas: Falta[];
}

/** Las faltas de una sede: las suyas y las de su gente. */
export interface FaltasDeSite {
  id: string;
  nombre: string;
  propias: Falta[];
  instructores: FaltasDeInstructor[];
  /** Lo de arriba sumado, que es el número que se enseña plegado. */
  total: number;
}

/** Un documento de cumplimiento que nadie del socio ha terminado de leer. */
export interface DocumentoPendiente {
  id: string;
  titulo: string;
  /** Lo que lleva leído QUIEN MIRA, para que sepa por dónde iba. */
  pct: number;
}

const ESQUEMAS = {
  organization: orgSchema,
  site: siteSchema,
  instructor: instructorSchema,
} as const;

/** Traduce las claves que devuelve camposPendientes() a algo legible. */
function faltasDe(
  entidad: EntidadNA,
  id: string,
  record: Record<string, unknown> | null | undefined,
  lang: Lang,
): Falta[] {
  const esquema = ESQUEMAS[entidad];
  const claves = camposPendientes(entidad, id, record, ds.marcasNA());
  const out: Falta[] = [];
  for (const campo of claves) {
    const def = esquema.find((f) => f.key === campo);
    if (!def) continue;   // un campo que cuenta pero no se pinta: no hay nada que enseñar
    out.push({
      campo,
      etiqueta: lang === 'es' ? def.label_es : def.label_en,
      seccion: def.section,
      obligatorio: def.state === 'required',
    });
  }
  // los obligatorios primero: son los que impiden guardar, el resto solo
  // bajan el porcentaje
  return out.sort((a, b) => Number(b.obligatorio) - Number(a.obligatorio));
}

const nombreDe = (i: Instructor) =>
  [i.EnglishFirstName, i.EnglishLastName].filter(Boolean).join(' ').trim() || i.InstructorId;

/** Los instructores afiliados a una sede. Mismo criterio que progresoPortal. */
function instructoresDe(siteId: string): Instructor[] {
  const ids = new Set(ds.listAssignments({ siteId }).map((a) => a.InstructorId));
  return ds.listInstructors().filter((i) => ids.has(i.InstructorId));
}

function faltasDeInstructores(instructores: Instructor[], lang: Lang): FaltasDeInstructor[] {
  return instructores
    .map((i) => ({
      id: i.InstructorId,
      nombre: nombreDe(i),
      faltas: faltasDe('instructor', i.InstructorId, i as unknown as Record<string, unknown>, lang),
    }))
    // quien está completo no sale: listar a los ocho para que cuatro digan
    // «nada» es ruido, no información
    .filter((x) => x.faltas.length > 0);
}

/** Lo que le falta a una sede.
 *
 *  `borrador` es la ficha a medio editar, igual que en progresoSite: sin
 *  él la lista no se movería mientras el socio escribe, y la mitad de la
 *  gracia es ver cómo baja. */
export function pendientesDeSite(
  siteId: string, lang: Lang, borrador?: Record<string, unknown> | null,
): FaltasDeSite | null {
  const guardado = ds.listSites().find((s) => s.SiteId === siteId);
  if (!guardado) return null;
  const site = borrador ? { ...guardado, ...borrador } as Site : guardado;
  const propias = faltasDe('site', siteId, site as unknown as Record<string, unknown>, lang);
  const instructores = faltasDeInstructores(instructoresDe(siteId), lang);
  return {
    id: siteId,
    nombre: site.SiteName || siteId,
    propias,
    instructores,
    total: propias.length + instructores.reduce((n, i) => n + i.faltas.length, 0),
  };
}

/** Lo que le falta a una organización, agrupado como se enseña. */
export interface FaltasDeOrg {
  propias: Falta[];
  sedes: FaltasDeSite[];
  documentos: DocumentoPendiente[];
  total: number;
}

export function pendientesDeOrg(
  orgId: string, lang: Lang, borrador?: Record<string, unknown> | null,
): FaltasDeOrg | null {
  const guardada = ds.listOrgs().find((o) => o.OrgId === orgId);
  if (!guardada) return null;
  const org = borrador ? { ...guardada, ...borrador } : guardada;
  const propias = faltasDe('organization', orgId, org as unknown as Record<string, unknown>, lang);

  /* Los instructores cuelgan de la SEDE, no de la organización: un
     instructor sin ninguna afiliación no pertenece a nadie —así lo define
     la migración 0009— y esos los ve solo ETC en Administración. Por eso
     aquí no hay grupo de «instructores sueltos»: no existirían. */
  const sedes = ds.listSites({ orgId })
    .map((s) => pendientesDeSite(s.SiteId, lang))
    .filter((s): s is FaltasDeSite => s !== null && s.total > 0);

  /* Un documento cuenta cuando alguien DEL SOCIO lo ha leído entero. `pct`
     es lo que lleva leído quien mira, y se enseña solo para que sepa por
     dónde iba: no es lo que decide si está cumplido. */
  const documentos: DocumentoPendiente[] = (ds.listCompliance(orgId) ?? [])
    .filter((d) => !d.cumplidoPorElSocio)
    .map((d) => ({ id: d.id, titulo: d.title || d.name, pct: d.pct ?? 0 }));

  return {
    propias,
    sedes,
    documentos,
    total: propias.length
      + sedes.reduce((n, s) => n + s.total, 0)
      + documentos.length,
  };
}
